import { useCallback, useEffect, useState, useRef } from "react";
import { GraphQLClient, SpreedlyResponseModel } from "../GraphQL.Default";
import SpreedlyFields from "../loan/complete/SpreedlyFields";
import { LoadScript } from "./ScriptHelper";
import { sleep } from "./Sleep";
import { Spinner } from "./Spinner";
import { last } from "./StringHelper";
import { useGraphQL } from "./useGraphQL";
import { ValidationMessage } from "./ValidationMessage";

export interface SpreedlyResult {
    spreedlyError: boolean,
    spreedlyToken: string,
    spreedlyTokenAdded: boolean
}

interface SpreedlyProps {
    callback: (result: SpreedlyResult) => void;
    customerName?: string,
    fundingCardBin?: string,
    fundingCardMonth?: string,
    fundingCardNumber?: string,
    fundingCardYear?: string
    submitRef: React.RefObject<HTMLButtonElement>;
}

export const Spreedly = (props: SpreedlyProps) => {
    const [spreedlyLoading, setSpreedlyLoading] = useState<boolean>(true);
    const [spreedlyError, setSpreedlyError] = useState<string>();
    const graphQLClient = useGraphQL(GraphQLClient);

    const { callback, customerName, fundingCardBin, fundingCardMonth, fundingCardNumber, fundingCardYear, submitRef } = props;

    const displayspreadly = useRef(true);

    const saveResponse = useCallback((success: boolean, response: string) => {
        var spreedlyResponse: SpreedlyResponseModel = { isSuccess: success, response: response};

        graphQLClient.saveSpreedlyResponse(spreedlyResponse);
    }, [graphQLClient]);

    const spreedlyFunctions = useCallback(async () => {
        window.Spreedly?.init("1akeQjjevpF3T6F5V0k0zwIXt6f", {
            "numberEl": "spreedly-number-div",
            "cvvEl": "spreedly-cvv"
        });

        window.Spreedly?.on("ready", () => {
            window.Spreedly?.setFieldType("number", "tel");
            window.Spreedly?.setNumberFormat("prettyFormat");
            window.Spreedly?.setStyle("number", "border: 1px solid #bebebe; height: 2.75rem; opacity: 1; width: 50%; font: normal normal normal 16px/16px 'Open Sans'; font-family: 'Open Sans', sans-serif; letter-spacing: 0px; line-height: 1.25rem;");
            setSpreedlyLoading(false);
        });

        window.Spreedly?.on("errors", (errors: any) => {
            setSpreedlyError(errors[0].message);
            saveResponse(false, errors[0].message);
            const result: SpreedlyResult = { spreedlyError: true, spreedlyToken: '', spreedlyTokenAdded: false };
            callback(result);
        });

        window.Spreedly?.on("paymentMethod", (token: string, pmData: any) => {
            if (pmData["first_six_digits"] !== fundingCardBin?.substring(0, 6) || pmData["last_four_digits"] !== fundingCardNumber?.substring(12)) {
                setSpreedlyError("The card you entered does not match the card on your application. Please re-enter or select a different funding method.");
            }
            else {
                setSpreedlyError('');
                saveResponse(true, JSON.stringify(pmData));
                const result: SpreedlyResult = { spreedlyError: false, spreedlyToken: token, spreedlyTokenAdded: true };
                callback(result);
            }
        });
    }, [callback, fundingCardBin, fundingCardNumber, saveResponse])

    const initSpreedly = useCallback(async () => {
        LoadScript('https://core.spreedly.com/iframe/iframe-v1.min.js');

        await sleep(500);
        while (window.Spreedly === undefined) {
            await sleep(1000);
        }

        spreedlyFunctions();
    }, [spreedlyFunctions]);

    useEffect(() => {
         if(window.Spreedly !== undefined){
             displayspreadly.current = false;
             spreedlyFunctions();
        } else {
            initSpreedly();
        }
    }, [initSpreedly,spreedlyFunctions]);


    const submitPaymentForm = useCallback(async () => {
        var cardFields: SpreedlyFields = {};
        cardFields.full_name = customerName;
        cardFields.month = fundingCardMonth;
        cardFields.year = fundingCardYear;

        window.Spreedly?.tokenizeCreditCard(cardFields);
    }, [customerName, fundingCardMonth, fundingCardYear]);

    return (
        <>
            {spreedlyLoading && <Spinner />}
            {displayspreadly &&
                <div className="ml-6 mt-3">
                    <p className="bold">
                        Please re-enter your card number ending in {last(fundingCardNumber, 4)} below
                    </p>
                    <label htmlFor="spreedly-number">Confirm card number</label>
                    <div id="spreedly-number-div" style={{ height: "3rem", width: "100%" }} /><br />
                    <ValidationMessage message={spreedlyError} name="spreedlyNumber" />
                    <div id="spreedly-cvv" hidden={true} />
                    <button ref={submitRef} className="hidden" onClick={submitPaymentForm} />
                </div>
            }
        </>
    )
};